import {
  PencilSquareIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  CursorArrowRaysIcon,
} from '@heroicons/react/20/solid';
import React from 'react';
import NoCoupon from '../../../assets/no-coupon.svg';
import {
  TWDataTable,
  EmptyState,
  CouponStatusBadge,
  TWButton,
} from '../../../components/ui';
import { headerCls } from '../../../data/columns';
import { MENU_FIELD_COPY } from '../constants';
import { menuNamesLabel } from './coupon-menus';

const PLATFORM_ICONS = {
  0: ComputerDesktopIcon,
  20: DevicePhoneMobileIcon,
  10: CursorArrowRaysIcon,
};

function formatCouponType(coupon, PublicCouponType) {
  if (coupon.type === PublicCouponType.SpendGetFreeItem) return 'Spend Get Free Item';
  if (coupon.type === PublicCouponType.SpendGetOff) {
    return coupon.discounttype === 0
      ? `Spend Get %${coupon.discountamount || ''} Off`
      : `Spend Get $${coupon.discountamount || ''} Off`;
  }
  if (coupon.type === PublicCouponType.BuyXGetY) {
    const details =
      coupon.buyItemCount && coupon.getItemCount
        ? ` (Buy ${coupon.buyItemCount} Get ${coupon.getItemCount})`
        : '';
    return `Buy X Get Y${details}`;
  }
  return '';
}

function PublicCouponList({
  publicCouponList,
  loading,
  publicCouponPagination,
  onPaginationChange,
  onEdit,
  PublicCouponType,
}) {
  if (!loading && publicCouponList.length === 0) {
    return (
      <EmptyState image={NoCoupon} title="No Public Campaigns" />
    );
  }

  const columns = [
    {
      title: <span className={headerCls}>Campaign Name</span>,
      dataIndex: 'name',
      key: 'name',
      render: (value) => (
        <span className="font-medium text-slate-900 dark:text-slate-100">{value}</span>
      ),
    },
    {
      title: <span className={headerCls}>Code</span>,
      dataIndex: 'code',
      key: 'code',
      render: (value) => (
        <span className="font-mono text-[13px] text-slate-600 dark:text-slate-300">{value}</span>
      ),
    },
    {
      title: <span className={headerCls}>Type</span>,
      key: 'type',
      render: (_, coupon) => (
        <span className="text-slate-600 dark:text-slate-300">
          {formatCouponType(coupon, PublicCouponType)}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Platform</span>,
      key: 'platforms',
      render: (_, coupon) => {
        const platforms = coupon.platforms?.split(',') || [];
        return (
          <div className="flex items-center gap-1.5">
            {platforms.map((p) => {
              const Icon = PLATFORM_ICONS[p];
              if (!Icon) return null;
              return (
                <span
                  key={p}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                >
                  <Icon className="h-3.5 w-3.5" />
                </span>
              );
            })}
          </div>
        );
      },
    },
    {
      title: <span className={headerCls}>{MENU_FIELD_COPY.column}</span>,
      key: 'menus',
      render: (_, coupon) => {
        const label = menuNamesLabel(coupon.menunames);
        const restricted = label !== MENU_FIELD_COPY.any;
        return (
          <span
            title={restricted ? label : undefined}
            className={`block max-w-[14rem] truncate text-sm ${restricted ? 'text-slate-600 dark:text-slate-300' : 'text-slate-500 dark:text-slate-400'}`}
          >
            {label}
          </span>
        );
      },
    },
    {
      title: <span className={headerCls}>Used</span>,
      dataIndex: 'usagecount',
      key: 'used',
      render: (value) => (
        <span className="tabular-nums text-slate-600 dark:text-slate-300">{value ?? 0}</span>
      ),
    },
    {
      title: <span className={headerCls}>Status</span>,
      dataIndex: 'status',
      key: 'status',
      render: (value) => <CouponStatusBadge status={value} />,
    },
    {
      title: <span className="sr-only">Actions</span>,
      key: 'actions',
      align: 'right',
      width: 60,
      render: (_, coupon) => (
        <TWButton
          variant="ghost"
          size="sm"
          icon={PencilSquareIcon}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(coupon);
          }}
          aria-label="Edit coupon"
        />
      ),
    },
  ];

  return (
    <TWDataTable
      columns={columns}
      data={publicCouponList}
      loading={loading}
      rowKey="id"
      onRowClick={(coupon) => onEdit(coupon)}
      pagination={{
        current: publicCouponPagination.pageNumber,
        pageSize: publicCouponPagination.pageSize,
        total: publicCouponPagination.totalRecords,
        showTotal: (total, range) =>
          `Showing ${range[0]} to ${range[1]} of ${total} results`,
        onChange: (page) => onPaginationChange(page),
      }}
    />
  );
}

export default PublicCouponList;
